/** @jsx jsx */

import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  Fragment as _,
} from 'react';
import { Editor } from 'slate-react';
import { css, jsx } from '@emotion/core';
//
import EditMarker from 'components/UI/EditMarker';
import { connect } from 'react-redux';
import renderMark from './renderMark';
import renderNode from './renderNode';
import renderInline from './renderInline';
import html from './serializer';
import getBlockById from 'services/apiHelpers/getBlockById';
import setBlock from 'services/apiHelpers/setBlockProps';
import setCurrentBlock from 'services/apiHelpers/setCurrentBlock';
import getStore from 'services/getStore';
import { isKeyHotkey } from 'is-hotkey';
import ReadOnlyText from './ReadOnlyText';
import bulletStyles from './bulletStyles';
import { TextToSpeech } from './TextToSpeech';
import { useFreshProps } from 'services/useFreshProps';
import { State } from 'store/store';
import { useSelector } from 'react-redux';
import hexToRgb from 'services/hexToRGB';
import { useModuleVersionOnCreation } from 'services/useModuleVersionOnCreation';
import { getDefaultLinkStyles } from 'store/theme';

const store = getStore();

const isBoldHotkey = isKeyHotkey('mod+b');
const isItalicHotkey = isKeyHotkey('mod+i');
const isUnderlinedHotkey = isKeyHotkey('mod+u');

export const placeholderText = 'Add content...';

let delayUpdate;

let CHAMELEON_COPIED_TEXT: string | undefined;

const schema = {
  marks: {
    variable: {
      isVoid: true,
    },
  },
};

const EditableText = ({ focusedNodeId, ...props }) => {
  const { id, className } = props;
  const { content = '', json } = props;

  const placeholderText = props.placeholder || 'Add content...';

  let enableTextToSpeechBtn = props?.enableTextToSpeechBtn;
  if (enableTextToSpeechBtn === undefined) {
    enableTextToSpeechBtn = true;
  }

  const {
    backgroundColor,
    bulletColor,
    borderRadius,
    textBackgroundOpacity,
    liTextColor = undefined,
    enableSecondaryLinkColors,
    enableCustomLinkColors,
    customLinkColors = {},
  } = useFreshProps(id, true);

  let background;
  if (backgroundColor) {
    const { r, g, b } = hexToRgb(backgroundColor);
    background = `rgba(${r}, ${g}, ${b}, ${textBackgroundOpacity * 0.01}`;
  }

  const textToSpeech = useSelector((state: State) => state.theme.textToSpeech);

  const moduleVersionOnCreation = useModuleVersionOnCreation();
  const defaultLinkStyles = getDefaultLinkStyles(moduleVersionOnCreation);

  const linkColors = useSelector((state: State) => {
    //
    // a spread of defaults / legacy links / and updated theme
    return {
      ...defaultLinkStyles.links,
      ...state.theme.links,
      ...state.theme?.typeSettings?.links,
      ...(enableCustomLinkColors && {
        ...customLinkColors,
      }),
    };
  });

  const { link, visited, hover, focus, active } = linkColors;

  const paragraphSpacing = store.getState().theme.paragraphSpacing || 16;

  const [state, setState] = useState({
    value: html.deserialize(content),
  });

  const text = useRef<HTMLDivElement | null>(null);

  const editor = useRef<any>(null);
  const clearedFormatting = useRef<boolean>(false);
  const selected = focusedNodeId === id;

  if (window.dev() && selected)
    window['__CURRENT_EDITOR_CLEAR_ALL_FORMATTING__'] = (content, id) => {
      setBlock(getBlockById(id), {
        content,
      });

      setState({
        value: html.deserialize(content),
      });
    };

  useEffect(() => {
    addEventListener('copy', onCopy);

    function onCopy(e) {
      let copied = (e.clipboardData || (window as any).clipboardData).getData(
        'text/plain'
      );

      CHAMELEON_COPIED_TEXT = `CHAMELEON_COPIED_TEXT=${copied}`;
    }

    return () => {
      removeEventListener('copy', onCopy);
    };
  }, []);

  useEffect(() => {
    if (!selected) {
      return;
    }
    parent.editor = editor.current;
  }, [selected]);

  useEffect(() => {
    if (content) {
      clearedFormatting.current = false;
    }
  }, [content]);

  useLayoutEffect(() => {
    //
    if (!text.current) {
      return;
    }

    //
    const innerText = text.current.innerText
      .toString()
      .replace(/\s\s+/g, ' ')
      .trim();

    const hasPlaceholder = innerText === placeholderText;

    text.current.style.textAlign = '';

    if (hasPlaceholder) {
      requestAnimationFrame(() => {
        if (text.current) {
          text.current.style.textAlign = 'left';
        }
      });
    }
  });

  const onChange = ({ value }) => {
    if (value.document !== state.value.document) {
      clearTimeout(delayUpdate);
      delayUpdate = setTimeout(() => {
        setBlock(getBlockById(id), {
          content: html.serialize(value),
        });
      }, 500);
    }

    setState({ value });
  };

  const onKeyDown = (e, editor, next) => {
    let mark;

    if (isBoldHotkey(e)) {
      mark = 'bold';
    } else if (isItalicHotkey(e)) {
      mark = 'italic';
    } else if (isUnderlinedHotkey(e)) {
      mark = 'underline';
    } else {
      return next();
    }

    e.preventDefault();
    editor.toggleMark(mark);
  };

  if (selected && editor.current) {
    parent.editor = editor.current;
  }

  const fontFamily = store.getState().theme.fontFamily || '';

  if (!window.dev()) {
    return (
      <ReadOnlyText
        id={`chameleon-text-${id}`}
        content={content}
        {...{
          className,
          borderRadius,
          background,
          bulletColor,
          liTextColor,
          linkColors,
          enableTextToSpeechBtn,
          ...props,
        }}
      />
    );
  }

  return (
    <div
      ref={text}
      id={`chameleon-text-${id}`}
      className={`chameleon-text${className ? ` ${className}` : ''}`}
      css={css`
			position: relative;
      width: 100%;
			padding: 12.5px 10px;

			${!selected && 'background: rgba(161, 188, 211, 0.15);'}
			box-shadow: ${selected ? '0 0 0 1px #ff0063' : 'none'};
		
      transition: background 0.1s, box-shadow 0.2s;

			&:hover {
				cursor: text;
				${!selected && 'box-shadow: 0 0 0 1px lightGrey;'}
				background: transparent;
      }

      a:link {
          color: ${link} !important;
        }

        a:visited {
          color: ${visited} !important;
        }

        a:hover {
          color: ${hover} !important;
        }

        a:focus-visible {
          color: ${focus} !important;

          outline-offset: 2px !important;
          outline: 1px solid ${focus} !important;
          text-decoration: none !important;
        }

        a:active {
          color: ${active} !important;
        }
		
			* {
        pointer-events: ${selected ? 'auto' : 'none'};
      }

      ${bulletStyles(bulletColor, liTextColor)}

      [data-slate-editor] {
        -webkit-user-modify: read-write !important;
      }
  
 

  `}
      style={
        background
          ? {
              borderRadius,
              background,
              padding: `${12.5 + borderRadius}px ${10 + borderRadius}px`,
            }
          : {}
      }
      onClick={e => {
        e.stopPropagation();

        if (selected) {
          return;
        }

        setCurrentBlock({ id });
        //

        const editorCurrent = editor.current;

        setTimeout(() => {
          if (!editorCurrent) {
            console.warn('where did the current text editor go !?!?');
            return;
          }
          //
          editorCurrent?.focus();
        }, 10);
        //
      }}
      //
    >
      <Editor
        key={id}
        id={id}
        ref={editor}
        value={state.value}
        placeholder={placeholderText || 'Add content...'}
        schema={schema}
        readOnly={!window.dev()}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onPaste={(e, $, next) => {
          let pasted = (
            e.clipboardData || (window as any).clipboardData
          ).getData('text/plain');

          if (CHAMELEON_COPIED_TEXT === `CHAMELEON_COPIED_TEXT=${pasted}`) {
            // console.log('pasting CHAMELEON_COPIED_TEXT');
            return next();
          }

          // console.log('pasting >> NOT << CHAMELEON_COPIED_TEXT');
          CHAMELEON_COPIED_TEXT = '';

          // break it
          const pastedToPs = pasted
            .split(/(\r\n|\n|\r)/g)
            .map(p => p.replace(/\s\s+/g, ' ').trim())
            .filter(p => p !== '')
            .map(p => `<p data-type="paragraph">${p}</p>`)
            .join('');

          const { document } = html.deserialize(pastedToPs);
          editor.current!.insertFragment(document);
        }}
        renderMark={renderMark}
        // renderNode
        renderBlock={renderNode}
        renderInline={renderInline}
      />

      {'speechSynthesis' in window && textToSpeech && enableTextToSpeechBtn && (
        <TextToSpeech id={id} content={content} {...props} />
      )}

      {selected && parent.updateEditor && parent.updateEditor(Math.random())}

      {!selected && <EditMarker />}
    </div>
  );
};

export default connect(
  ({ focusedNodeId }: { focusedNodeId: string | number }) => ({
    focusedNodeId,
    [Math.random()]: '',
  })
)(EditableText);
